import { Badge, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { Dayjs } from "dayjs";
import React, { ReactNode } from "react";
import { DeliveryReport } from "types";
import { PhoneWithTooltip } from "./PhoneWithTooltip";
import { PreviewMessageTemplateDrawer } from "@/components/Customers/Templates/PreviewMessageTemplateDrawer";
import { useConversation } from "@/hooks/resources/useConversation";
import {
    getCreditsLabel,
    getMessageText,
    getPhoneDisplay,
    getRecipientName,
    getRelativeTime,
    getStatusColor,
    getStatusLabel,
} from "@/models/DeliveryReport";

interface MessageDeliveryDrawerProps {
    report?: DeliveryReport;
    isOpen: boolean;
    onClose: () => void;
}

export const MessageDeliveryDrawer = ({ report, isOpen, onClose }: MessageDeliveryDrawerProps) => {
    return (
        <PreviewMessageTemplateDrawer
            isOpen={isOpen}
            classNames={{ dialog: "z-40", children: "!p-0", content: "mt-0" }}
            onClose={onClose}
        >
            {/* Separate component so the conversation is never fetched for a drawer nobody opened. */}
            {isOpen && report ? <MessageDeliveryDetails report={report} /> : <span />}
        </PreviewMessageTemplateDrawer>
    );
};

interface MessageDeliveryDetailsProps {
    report: DeliveryReport;
}

const MessageDeliveryDetails = ({ report }: MessageDeliveryDetailsProps) => {
    const { conversation, isLoadingConversation } = useConversation(report.conversation.id, { enabled: true });
    const messageText = getMessageText(conversation, report.message);
    const recipientName = getRecipientName(conversation, report.message);

    const sentTo = getPhoneDisplay(report.sentTo, report.sentToNormalized);
    // `sentFrom` has no separate normalized field like `sentTo` does, so strip a leading `+` before
    // reusing it as the normalized argument; otherwise an E.164 value would get a second `+` prepended.
    const sentFrom = getPhoneDisplay(report.sentFrom, report.sentFrom?.replace(/^\+/, "") ?? null);

    return (
        <div className="ui-message-delivery-details divide-y divide-gray-light">
            <header className="space-y-4 px-10 py-6">
                <div className="flex items-center justify-between">
                    <h3 className="no-translate tabular-nums">
                        <PhoneWithTooltip phone={sentTo} fallback="Unknown recipient" />
                        {/* Name resolves after the phone number, once the conversation loads; render nothing until it does. */}
                        {recipientName && <span className="no-translate text-sm text-gray-dark"> {recipientName}</span>}
                    </h3>
                    <Badge color={getStatusColor(report.status)}>{getStatusLabel(report.status)}</Badge>
                </div>

                <dl className="grid grid-cols-[auto,minmax(0,1fr),auto,minmax(0,1fr)] items-baseline gap-x-4 gap-y-2">
                    <DetailRow label="From" className="tabular-nums">
                        <PhoneWithTooltip phone={sentFrom} fallback="--" />
                    </DetailRow>
                    <DetailRow label="Credits" className="tabular-nums">
                        {getCreditsLabel(report.credits)}
                    </DetailRow>
                    <DetailRow label="Provider ID" className="tabular-nums">
                        {report.remoteId ?? "Never reached the provider"}
                    </DetailRow>
                    <TimestampDetailRow label="Created" date={report.createdAt} />
                    <TimestampDetailRow label="Updated" date={report.updatedAt} />
                    {report.deliveredAt && <TimestampDetailRow label="Delivered" date={report.deliveredAt} />}
                    {report.error && <DetailRow label="Error">{report.error}</DetailRow>}
                </dl>
            </header>

            {/* The header above is already complete from the row's own data; only this region waits. */}
            <div className="px-10 py-6">
                <h4 className="mb-2 text-sm font-semibold text-gray-darker">Message</h4>
                {isLoadingConversation ? (
                    <Skeleton height={72} data-testid="message-delivery-text-skeleton" />
                ) : (
                    <p className="no-translate whitespace-pre-line">
                        {messageText ?? <span className="text-gray-dark">Message text unavailable</span>}
                    </p>
                )}
            </div>
        </div>
    );
};

interface DetailRowProps {
    label: string;
    children: ReactNode;
    className?: string;
}

const DetailRow = ({ label, children, className }: DetailRowProps) => {
    return (
        <>
            <dt className="text-sm text-gray-dark">{label}</dt>
            <dd className={clsx("no-translate", className)}>{children}</dd>
        </>
    );
};

interface TimestampDetailRowProps {
    label: string;
    date: Dayjs;
}

// The column is `minmax(0, 1fr)`, not a bare `1fr`, so a long relative string like "about 2 months
// ago" truncates via `min-w-0` instead of growing the grid track and pushing the next pair over.
const TimestampDetailRow = ({ label, date }: TimestampDetailRowProps) => {
    return (
        <DetailRow label={label} className="flex min-w-0 items-baseline gap-2">
            <span className="shrink-0 tabular-nums">{formatDate(date, DateFormat.DATE_TIME_SHORT)}</span>
            <span className="min-w-0 truncate text-xs text-gray-dark">{getRelativeTime(date)}</span>
        </DetailRow>
    );
};
